import React, { useState } from "react";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Download, List, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useBlobUrl, downloadDoc } from "./acqUi";

function PageImage({ doc, n, w, className, testId }) {
  const url = useBlobUrl(doc ? `/acquisition/documents/${doc.id}/pages/${n}?w=${w}` : null);
  if (!url) return <div className={`flex items-center justify-center bg-slate-100 ${className}`}><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (url === "error") return <div className={`flex items-center justify-center bg-slate-100 text-xs text-slate-500 ${className}`}>Page unavailable</div>;
  return <img src={url} alt={`Page ${n}`} className={className} draggable={false} onContextMenu={(e) => e.preventDefault()} data-testid={testId} />;
}

function Toc({ toc, go }) {
  if (!toc?.length) return null;
  return (
    <Popover>
      <PopoverTrigger asChild><Button variant="outline" size="sm" data-testid="flipbook-toc"><List className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Contents</span></Button></PopoverTrigger>
      <PopoverContent className="w-72 p-1 bg-white max-h-80 overflow-y-auto">
        {toc.map((t) => (
          <button key={t.title} onClick={() => go(t.page)} className="w-full flex justify-between gap-3 text-left text-sm px-2.5 py-1.5 rounded hover:bg-slate-100" data-testid={`flipbook-toc-${t.page}`}>
            <span className="text-slate-700">{t.title}</span><span className="text-slate-400 tabular-nums">{t.page}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function Controls({ page, total, go, zoom, setZoom, toc, onFull, doc }) {
  const dl = async () => { try { await downloadDoc(doc); } catch { toast.error("Download isn't available"); } };
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => go(page - 1)} disabled={page <= 1} aria-label="Previous page" data-testid="flipbook-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-sm text-slate-700 tabular-nums min-w-[70px] text-center" data-testid="flipbook-page-indicator">{page} of {total}</span>
        <Button variant="outline" size="icon" onClick={() => go(page + 1)} disabled={page >= total} aria-label="Next page" data-testid="flipbook-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="flex items-center gap-2">
        <Toc toc={toc} go={go} />
        {setZoom && <>
          <Button variant="outline" size="icon" onClick={() => setZoom(Math.max(1, zoom - 0.5))} disabled={zoom <= 1} aria-label="Zoom out" data-testid="flipbook-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => setZoom(Math.min(2.5, zoom + 0.5))} disabled={zoom >= 2.5} aria-label="Zoom in" data-testid="flipbook-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
        </>}
        {onFull && <Button variant="outline" size="sm" onClick={onFull} data-testid="flipbook-open-full"><Maximize2 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Open Full Guide</span></Button>}
        {doc?.downloadable && <Button variant="outline" size="sm" onClick={dl} data-testid="flipbook-download"><Download className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Download Original</span></Button>}
      </div>
    </div>
  );
}

function Thumbs({ doc, total, page, go }) {
  return (
    <div className="flex gap-2 overflow-x-auto tr-scroll pb-1" data-testid="flipbook-thumbs">
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <button key={n} onClick={() => go(n)} className={`shrink-0 w-14 rounded border-2 overflow-hidden transition-colors ${n === page ? "border-[#006090]" : "border-transparent hover:border-slate-300"}`} data-testid={`flipbook-thumb-${n}`} aria-label={`Page ${n}`}>
          <PageImage doc={doc} n={n} w={160} className="w-full aspect-[8.5/11] object-cover" />
        </button>
      ))}
    </div>
  );
}

export function FullViewer({ doc, toc, open, onOpenChange, page, setPage }) {
  const [zoom, setZoom] = useState(1);
  const total = doc?.pages || 1;
  const go = (n) => setPage(Math.min(total, Math.max(1, n)));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-5xl w-[96vw] h-[92vh] flex flex-col p-4 gap-3" data-testid="flipbook-full-viewer" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2 pr-8"><Lock className="w-4 h-4 text-slate-400" />{doc?.title} · {doc?.version_label}</DialogTitle></DialogHeader>
        <Controls page={page} total={total} go={go} zoom={zoom} setZoom={setZoom} toc={toc} doc={doc} />
        <div className="flex-1 overflow-auto bg-slate-100 rounded-lg p-3" data-testid="flipbook-full-canvas">
          <div style={{ width: `${zoom * 100}%` }} className="mx-auto max-w-none">
            <PageImage doc={doc} n={page} w={Math.round(1400 * zoom)} className="w-full shadow-lg bg-white" testId="flipbook-full-page" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function GuideFlipbook({ doc, toc, page, setPage, onOpenFull }) {
  const total = doc?.pages || 1;
  const go = (n) => setPage(Math.min(total, Math.max(1, n)));
  if (!doc) return <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="flipbook-missing">The Program Guide isn't available yet.</div>;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm space-y-4" data-testid="program-guide-flipbook">
      <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-4 items-center">
        <div className="hidden lg:block" />
        <div className="relative mx-auto w-full max-w-[520px]">
          <div className="absolute inset-0 translate-x-2 translate-y-2 rounded-md bg-slate-200" aria-hidden />
          <button onClick={() => onOpenFull(page)} className="relative block w-full rounded-md overflow-hidden ring-1 ring-slate-200 shadow-xl bg-white" aria-label="Open full guide" data-testid="flipbook-page">
            <PageImage key={page} doc={doc} n={page} w={1100} className="w-full aspect-[8.5/11] object-contain bg-white animate-in fade-in duration-300" testId="flipbook-page-image" />
          </button>
        </div>
        <div className="hidden lg:flex flex-col gap-2 text-sm text-slate-600 self-start pt-6">
          <p className="flex items-center gap-1.5 text-xs text-slate-500"><Lock className="w-3.5 h-3.5" />Proprietary · secure viewer</p>
          {toc.slice(0, 8).map((t) => <button key={t.title} onClick={() => go(t.page)} className={`text-left hover:text-[#006090] ${t.page === page ? "text-[#006090] font-semibold" : ""}`} data-testid={`flipbook-side-toc-${t.page}`}>{t.title}</button>)}
        </div>
      </div>
      <Controls page={page} total={total} go={go} toc={toc} onFull={() => onOpenFull(page)} doc={doc} />
      <Thumbs doc={doc} total={total} page={page} go={go} />
    </div>
  );
}
